// src/platform/OrgDetailPanel.jsx
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import OrgEntitlementsPanel from "./OrgEntitlementsPanel";
import OrgSeatsPanel from "./OrgSeatsPanel";
import OrgAnalyticsPanel from "./OrgAnalyticsPanel";
import OrgApiKeysPanel from "./OrgApiKeysPanel";
import OrgFeatureGrantsPanel from "./OrgFeatureGrantsPanel";

import { ORG_ROLE_LABEL } from "../constants/orgRoles";
import { LICENSE_TIER_LABEL } from "../constants/licenseTiers";

const TABS = ["settings", "users", "seats", "imagery", "api-keys", "analytics", "features"]

// ── THE LABEL IS NOT THE KEY ────────────────────────────────────────────────
//
// The tabs used to render their own id, uppercased by CSS, so "analytics" read
// ANALYTICS. It is the product entitlement tab -- WellFinder, AssetID,
// PadRekko and now Portal are granted there, and only some of them are
// analytics -- so it reads PRODUCTS.
//
// THE KEY IS LEFT ALONE. 'analytics' is what activeTab switches on below and
// what the endpoint behind it is called (/analytics-grants, OrgAnalyticsPanel).
// Renaming the display string is a copy change; renaming the key would be a
// rename of the panel, its route and its table, which this is not.
const TAB_LABEL = { analytics: "products" }

export default function OrgDetailPanel({ org, onClose, onUpdated }) {
    const [activeTab, setActiveTab] = useState("settings");
    const [status, setStatus] = useState(org.status);
    const [saving, setSaving] = useState(false);

    const [orgUsers, setOrgUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    async function loadOrgUsers() {
        setLoadingUsers(true);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/orgs/${org.id}/users`);
            const data = await res.json();
            setOrgUsers(data.users || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingUsers(false);
        }
    }

    async function save() {
        setSaving(true);
        try {
            await fetchWithAuth(`/api/v1/platform/orgs/${org.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status }),
            });

            await onUpdated();
            onClose();
        } catch (e) {
            console.error(e);
            alert("Save failed");
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        if (activeTab === "users") {
            loadOrgUsers();
        }
    }, [activeTab]);

    return (
        <div className="fixed inset-y-0 right-0 z-50 w-3/4 bg-bg-soft border-l border-bg-border shadow-xl flex flex-col">

            {/* HEADER */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-bg-border">
                <div>
                    <h2 className="text-sm font-semibold">{org.name}</h2>
                    <p className="text-xs text-gray-400">Organization Details</p>
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-200"
                >
                    <X size={18} />
                </button>
            </div>

            {/* TABS */}
            <div className="flex border-b border-bg-border overflow-x-auto">
                {TABS.map(t => (
                    <button
                        key={t}
                        onClick={() => setActiveTab(t)}
                        className={`
              flex-1 px-3 py-2 text-xs uppercase tracking-wide whitespace-nowrap
              border-b-2
              ${activeTab === t
                                ? "border-accent-action text-accent-action"
                                : "border-transparent text-gray-400 hover:text-gray-200"}
            `}
                    >
                        {TAB_LABEL[t] ?? t}
                    </button>
                ))}
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">

                {/* SETTINGS TAB */}
                {activeTab === "settings" && (
                    <>
                        <div>
                            <label className="block text-xs mb-1 text-gray-400">
                                Status
                            </label>
                            <select
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                            >
                                <option value="active">active</option>
                                <option value="suspended">suspended</option>
                            </select>
                            <p className="text-xs text-fg-secondary mt-1">
                                Suspending an org disables access for all members and revokes API keys.
                            </p>
                        </div>
                    </>
                )}

                {/* USERS TAB */}
                {activeTab === "users" && (
                    <div className="space-y-2">
                        {loadingUsers ? (
                            <div className="text-xs text-gray-400">Loading users…</div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead className="text-gray-400">
                                    <tr>
                                        <th className="text-left py-1">Email</th>
                                        <th className="text-left py-1">Role</th>
                                        <th className="text-left py-1">License</th>
                                        <th className="text-left py-1">Status</th>
                                        <th className="text-left py-1">Last Login</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orgUsers.map(u => (
                                        <tr
                                            key={u.user_id}
                                            className="border-t border-bg-border"
                                        >
                                            <td className="py-1">{u.email}</td>
                                            <td className="py-1">{ORG_ROLE_LABEL[u.role] ?? u.role}</td>
                                            <td className="py-1">
                                                {u.tier_code ? (
                                                    <span className="text-accent-info">
                                                        {LICENSE_TIER_LABEL[u.tier_code] ?? u.tier_code}
                                                    </span>
                                                ) : (
                                                    <span className="text-fg-secondary italic">Unassigned</span>
                                                )}
                                            </td>
                                            <td className="py-1">
                                                <span
                                                    className={`px-2 py-0.5 rounded border
                                                    ${u.membership_status === "active"
                                                            ? "border-green-500/40 text-green-400"
                                                            : "border-red-500/40 text-red-400"}
                                                    `}
                                                >
                                                    {u.membership_status}
                                                </span>
                                            </td>
                                            <td className="py-1 text-gray-400">
                                                {u.last_login_at
                                                    ? new Date(u.last_login_at).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {activeTab === "seats" && (
                    <OrgSeatsPanel orgId={org.id} />
                )}

                {/* ENTITLEMENTS TAB */}
                {activeTab === "imagery" && (
                    <OrgEntitlementsPanel orgId={org.id} />
                )}

                {/* API KEYS TAB */}
                {activeTab === "api-keys" && (
                    <OrgApiKeysPanel orgId={org.id} />
                )}

                {activeTab === "analytics" && (
                    <OrgAnalyticsPanel orgId={org.id} />
                )}

                {activeTab === "features" && (
                    <OrgFeatureGrantsPanel orgId={org.id} />
                )}

            </div>

            {/* FOOTER — hide save button on api-keys tab (no save needed) */}
            {activeTab !== "api-keys" && (
                <div className="p-4 border-t border-bg-border flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1 rounded border border-bg-border text-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="
                px-3 py-1 rounded
                bg-accent-action/20 border border-accent-action/60
                text-accent-action
              "
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}